/**
 * InvoicesPage — who owes the shop money.
 *
 * The app could bill and the customer could pay, and there was still no way to ask
 * "what is unpaid". Invoices opened only at /owner/invoices/:id, so you needed the
 * number before you could look at it, and the route without one was deliberately
 * sent to the dashboard. The list endpoint existed the whole time.
 *
 * Unpaid first, oldest first within that, because the question is always "who owes me
 * and for how long" — never "what did we invoice most recently".
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Typography, Alert, Input, Space, Empty, Button } from 'antd';
import { FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../services/invoiceService';
import type { Invoice } from '../services/paymentService';

const { Title, Text } = Typography;

const money = (n?: number) =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Unpaid is the only status that costs the shop something today. */
const OUTSTANDING = new Set(['draft']);

const STATUS_COLOUR: Record<string, string> = {
  draft: 'orange',
  paid: 'green',
  void: 'default',
  refunded: 'purple',
};

/** How long the money has been outstanding, in the words someone would use. */
function ageOf(at?: string): { days: number; label: string } {
  if (!at) return { days: 0, label: '—' };
  const then = new Date(at.includes('T') ? at : `${at.replace(' ', 'T')}Z`);
  if (Number.isNaN(then.getTime())) return { days: 0, label: '—' };
  const days = Math.max(0, Math.floor((Date.now() - then.getTime()) / 86_400_000));
  if (days === 0) return { days, label: 'today' };
  if (days === 1) return { days, label: '1 day' };
  return { days, label: `${days} days` };
}

export const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const rows = await invoiceService.getAllInvoices();
      setInvoices(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (err: any) {
      // An unreadable list and an empty one are different facts. Saying "no invoices"
      // for a failed request sends someone looking for a billing bug that is not there.
      setError(err?.response?.data?.error ?? 'Could not read the invoices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? invoices.filter((i) => {
          const customer = i.customer
            ? `${i.customer.first_name ?? ''} ${i.customer.last_name ?? ''}`
            : '';
          return (
            String(i.invoice_number ?? '').toLowerCase().includes(q) ||
            customer.toLowerCase().includes(q)
          );
        })
      : invoices;

    // Outstanding first, then oldest first. The sort IS the answer to the question
    // this page exists for, so it is not left to the reader to re-sort.
    return [...filtered].sort((a, b) => {
      const aOut = OUTSTANDING.has(a.status) ? 0 : 1;
      const bOut = OUTSTANDING.has(b.status) ? 0 : 1;
      if (aOut !== bOut) return aOut - bOut;
      return ageOf(b.created_at).days - ageOf(a.created_at).days;
    });
  }, [invoices, search]);

  const owed = useMemo(
    () =>
      invoices
        .filter((i) => OUTSTANDING.has(i.status))
        .reduce((sum, i) => sum + (i.total_amount || 0), 0),
    [invoices]
  );

  const columns = [
    {
      title: 'Invoice',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (n: string, row: Invoice) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/owner/invoices/${row.id}`)}>
          {n}
        </Button>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_: unknown, row: Invoice) =>
        row.customer ? `${row.customer.first_name ?? ''} ${row.customer.last_name ?? ''}`.trim() : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag color={STATUS_COLOUR[s] ?? 'default'}>{s}</Tag>,
    },
    {
      title: 'Age',
      key: 'age',
      render: (_: unknown, row: Invoice) => {
        const age = ageOf(row.created_at);
        const overdue = OUTSTANDING.has(row.status) && age.days >= 30;
        return <Text type={overdue ? 'danger' : undefined}>{age.label}</Text>;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right' as const,
      render: (v: number) => money(v),
    },
  ];

  return (
    <div style={{ padding: '16px 12px', maxWidth: '100%' }}>
      <Space direction="vertical" size="large" style={{ width: '100%', minWidth: 0 }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>
            <FileTextOutlined /> Invoices
          </Title>
          <Text type="secondary">
            {owed > 0
              ? `${money(owed)} outstanding across ${invoices.filter((i) => OUTSTANDING.has(i.status)).length} invoice(s).`
              : 'Nothing outstanding.'}
          </Text>
        </div>

        {error && <Alert type="error" showIcon message={error} />}

        <Card
          styles={{ body: { minWidth: 0, overflowX: 'auto' } }}
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => void load()} size="small" aria-label="Refresh" />
          }
          title={
            <Input.Search
              placeholder="Invoice number or customer"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          }
        >
          {!loading && !error && invoices.length === 0 ? (
            <Empty description="No invoices yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={rows}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 720 }}
              size="middle"
            />
          )}
        </Card>
      </Space>
    </div>
  );
};

export default InvoicesPage;
